
import React from 'react';

const AnimatedNumber = ({ value, duration = 1500, suffix = '', prefix = '' }) => {
    const [displayValue, setDisplayValue] = React.useState(0);

    React.useEffect(() => {
        let startTime;
        const startValue = 0;
        const endValue = Math.round(Number(value) || 0);

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);

            // Easing function for smooth effect
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);

            const current = startValue + (endValue - startValue) * easeOutQuart;

            // Format to match original data type (int vs float)
            // Assuming integers for now based on typical dashboard data, 
            // but could adapt if needed.
            setDisplayValue(Math.round(current));

            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        };

        window.requestAnimationFrame(step);
    }, [value, duration]);

    return (
        <span>
            {prefix}{displayValue}{suffix}
        </span>
    );
};

const MonthlyProgress = ({ data: rawData }) => {
    const data = rawData || { planned: 0, actual: 0, variance: 0, delayDays: 0, executionScore: 0 };
    // Prevent crash if data is null (e.g. during loading)

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Planned Progress */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PLANNED PROGRESS</h3>
                <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                        <AnimatedNumber value={data.planned} suffix="%" />
                    </span>
                </div>
            </div>

            {/* Actual Progress */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">ACTUAL PROGRESS</h3>
                <div className="flex items-end gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                        <AnimatedNumber value={data.actual} suffix="%" />
                    </span>
                    <span className={`text-sm font-medium mb-1 ${data.variance < 0 ? 'text-red-500' : 'text-green-500'}`}>
                        <AnimatedNumber value={Math.abs(data.variance)} prefix={data.variance < 0 ? '-' : '+'} suffix="%" /> variance
                    </span>
                </div>
            </div>

            {/* Delay Days */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">DELAY DAYS</h3>
                <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-red-500">
                        <AnimatedNumber value={data.delayDays} />
                    </span>
                </div>
            </div>

            {/* Execution Score */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">EXECUTION SCORE</h3>
                <div className="flex items-end gap-1">
                    <span className="text-4xl font-bold text-gray-900">
                        <AnimatedNumber value={data.executionScore} />
                    </span>
                    <span className="text-sm font-medium text-gray-400 mb-1">out of 100</span>
                </div>
            </div>
        </div>
    );
};

export default MonthlyProgress;
